import StructureLabel from 'LuxKit/Label/StructureLabel'
import ClassicRewardsSealIcon from 'LuxKit/Icons/loyalty/ClassicRewardsSealIcon'
import switchFunc from 'lib/function/switchFunc'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import useInsuranceLabels from 'hooks/Insurance/useInsuranceLabels'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'

/**
 * The base label is a fixed-height inline-flex pill that forbids wrapping. The
 * Classic Rewards label adds the program name (e.g. "Economy Société Rewards"),
 * which can overflow narrow tile widths — keep the seal icon beside the text
 * (no flex wrap) but allow the text itself to break across lines.
 */
const WrappingClassicRewardLabel = styled(StructureLabel)`
  white-space: normal;
  height: auto;
  min-height: ${rem(24)};
  padding-block: ${rem(2)};
  text-align: start;
  align-items: center;
`

const cabinToLabelVariant = switchFunc<React.ComponentProps<typeof StructureLabel>['variant'], string>({
  Business: 'dark',
  First: 'exclusive',
}, 'premium')

interface Props {
  fareFamily: App.FlightFareFamily
  isClassicReward?: boolean
}

function FareFamilyClassLabel(props: Props) {
  const { fareFamily, isClassicReward } = props
  const intl = useIntl()
  const { protectedFlightsName } = useInsuranceLabels()

  const label = useMemo(() => {
    const description = fareFamily.fareFamily.description
    if (fareFamily.withEmbeddedInsurance) {
      return protectedFlightsName
    }
    if (isClassicReward) {
      return intl.formatMessage(
        {
          id: '9J4V98kEbYKmvmHhX5uS',
          defaultMessage: '{cabin} {programName} Rewards',
          description: 'Cabin class pill label on the checkout review flight tile when the flight is a Classic Rewards redemption (e.g., "Economy Société Rewards"). cabin is the fare-family cabin name, programName is the configured loyalty program (e.g., Société).',
        },
        { cabin: description, programName: getLoyaltyProgramDisplayName() },
      )
    }
    return description
  }, [fareFamily.fareFamily.description, fareFamily.withEmbeddedInsurance, protectedFlightsName, isClassicReward, intl])

  const LabelComponent = isClassicReward ? WrappingClassicRewardLabel : StructureLabel
  const labelVariant = isClassicReward ? 'maldives-charter' : cabinToLabelVariant(fareFamily.cabin)

  return (<LabelComponent
    variant={labelVariant}
    startIcon={isClassicReward ? <ClassicRewardsSealIcon /> : undefined}
  >
    {label}
  </LabelComponent>)
}

export default FareFamilyClassLabel
